// S-2 `/signup` — Регистрация. Source: stage-4-screens.md §5.2 + auth-otp-verification §4.2.
//
// Поток: SignupSchema → useRegisterMutation → AwaitVerifyContext.set({mode:'signup'})
// → setLocation('/verify-code'). Пароль в контекст НЕ кладём (OTP-флоу: токен
// выдаёт verify-code). 422 → setError per поле.
//
// HEAL-69: перед созданием аккаунта человек подтверждает согласие с Условиями и
// Политикой. Галочка живёт в локальном state, а не в SignupSchema: на бэкенд она не
// уходит — юридическая запись о согласии делается позже, на экране /consent, где
// фиксируются версия и три флага. Здесь это барьер интерфейса, а не второй consent.

import { useState, type MouseEvent } from 'react';
import { Link, useLocation, useSearch } from 'wouter';
import { flushSync } from 'react-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SignupSchema, type SignupForm } from '@/api/schemas';
import { useRegisterMutation } from '@/api/hooks/auth';
import { applyValidationErrors, useApiErrorToast } from '@/api/error';
import { useAwaitVerifyContext } from '@/context/AwaitVerifyContext';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { LinkText } from '@/components/layout/LinkText';
import { LegalSheet } from '@/components/legal/LegalSheet';
import { FormField } from '@/components/forms/FormField';
import { SubmitButton } from '@/components/forms/SubmitButton';
import { Checkbox } from '@/components/ui/checkbox';
import type { LegalKind } from '@/content/legal';

export function Signup() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const registerMutation = useRegisterMutation();
  const awaitVerify = useAwaitVerifyContext();
  const showErrorToast = useApiErrorToast();

  const [legalAccepted, setLegalAccepted] = useState(false);
  const [openDoc, setOpenDoc] = useState<LegalKind | null>(null);

  const prefillEmail = new URLSearchParams(search).get('email') ?? '';

  // Ссылки на документы лежат внутри <label> чекбокса: без гашения события клик по
  // «Условиям» заодно переключал бы галочку — человек открыл почитать, а согласие уже
  // проставлено (или снято) за него.
  const openDocHandler = (kind: LegalKind) => (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenDoc(kind);
  };

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<SignupForm>({
    resolver: zodResolver(SignupSchema),
    defaultValues: {
      email: prefillEmail,
      password: '',
      first_name: '',
      last_name: '',
    },
  });

  const onSubmit = handleSubmit(async (form) => {
    const body = {
      email: form.email,
      password: form.password,
      first_name: form.first_name,
      last_name: form.last_name,
    };
    try {
      await registerMutation.mutateAsync(body);
      flushSync(() => {
        awaitVerify.set({ mode: 'signup', email: form.email });
      });
      setLocation('/verify-code');
    } catch (err) {
      const applied = applyValidationErrors<keyof SignupForm>(err, setError);
      if (!applied) showErrorToast(err);
    }
  });

  return (
    <MobileFrame>
      <ScreenHeader onBack={() => setLocation('/')} title="Назад" />

      {/* Gradient-hero + blur-blob (палитра лендинга, как на /verify-code). */}
      <div className="relative flex-1 overflow-y-auto overflow-x-hidden bg-gradient-to-b from-primary/5 to-transparent px-6 pb-8 pt-2">
        <div className="pointer-events-none absolute -right-16 -top-10 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-surface bg-gradient-to-br from-primary to-primary/70 text-xl font-bold text-primary-foreground shadow-lg shadow-primary/25">
              H
            </div>
            <h1 className="mb-1 text-hl-title font-bold leading-tight tracking-tight">HealthLab</h1>
            <p className="text-sm text-muted-foreground">Создайте аккаунт</p>
          </div>

        <div className="mb-5 flex rounded-control bg-muted p-1">
          <Link
            href="/signin"
            className="flex-1 rounded-md px-4 py-2 text-center text-sm font-medium text-muted-foreground"
          >
            Войти
          </Link>
          <div className="flex-1 rounded-md bg-background px-4 py-2 text-center text-sm font-medium shadow-sm">
            Зарегистрироваться
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <FormField
            label="Фамилия"
            type="text"
            autoComplete="family-name"
            placeholder="Введите фамилию"
            hint="Как в документах — сверяем с анализами."
            error={errors.last_name?.message}
            {...register('last_name')}
          />
          <FormField
            label="Имя"
            type="text"
            autoComplete="given-name"
            placeholder="Введите имя"
            error={errors.first_name?.message}
            {...register('first_name')}
          />
          <FormField
            label="Email"
            type="email"
            autoComplete="email"
            placeholder="ваш@email.com"
            error={errors.email?.message}
            {...register('email')}
          />
          <FormField
            label="Пароль"
            type="password"
            autoComplete="new-password"
            placeholder="Минимум 8 символов"
            hint="Минимум 8 символов: буквы и цифры"
            error={errors.password?.message}
            {...register('password')}
          />
          {/* HEAL-50: поле «Повторите пароль» убрано — см. комментарий у SignupSchema. */}

          {/* HEAL-69: согласие с документами перед созданием аккаунта. Документы
              открываются листом поверх формы — введённое не теряется, см. LegalSheet. */}
          <label
            htmlFor="legal_accepted"
            className="flex cursor-pointer gap-3 rounded-control border border-border bg-card p-3"
          >
            <Checkbox
              id="legal_accepted"
              checked={legalAccepted}
              onCheckedChange={(checked) => setLegalAccepted(checked === true)}
              className="mt-0.5"
            />
            <span className="min-w-0 flex-1 text-hl-small leading-relaxed text-muted-foreground">
              Я принимаю{' '}
              <LinkText onClick={openDocHandler('tos')}>Условия использования</LinkText>
              {' и '}
              <LinkText onClick={openDocHandler('privacy')}>
                Политику конфиденциальности
              </LinkText>
              .
            </span>
          </label>

          <SubmitButton isPending={registerMutation.isPending} disabled={!legalAccepted}>
            Создать аккаунт
          </SubmitButton>
        </form>

        <LegalSheet kind={openDoc} onClose={() => setOpenDoc(null)} />

          <p className="mt-5 text-center text-hl-small text-muted-foreground">
            Уже есть аккаунт?{' '}
            <Link href="/signin" className="font-semibold text-primary-text">
              Войти
            </Link>
          </p>
        </div>
      </div>
    </MobileFrame>
  );
}
